import { useEffect, useState } from "react";
import { Chart } from "../models/company.model";
import { getReportGroup } from "../actions/report.action";
import _ from "lodash";
import { TableDateRangeEnum } from "../models/company.model";
import moment from "moment";
import { Period, ReportDataModel, ReportModel } from "../models/report.model";

export function useGroupBy(params: { chart: Chart; reports?: ReportDataModel; isSingle?: boolean }) {
  const { data, groupBy, showInHierarchy, storeLabelTypes } = params.chart || {};
  const companyId = params.reports?.main.company_id;

  const [loading, setLoading] = useState(false);
  const [groupedData, setGroupedData] = useState<ReportModel[][]>([[]]);

  useEffect(() => {
    if (!companyId || !groupBy) return;
    const keysNeeded: string[] = [];
    data.forEach((d) => {
      const matches = d.expression.match(varsInExpression);
      matches?.forEach((m) => keysNeeded.push(m));
    });
    setLoading(true);
    Promise.all(
      (params.isSingle ? [data[0]] : data).map((d) => {
        return getReportGroup({
          id: companyId,
          groupBy,
          keysNeeded: _.uniq(keysNeeded),
          period: getPeriod(d.tableDateRange),
          showInHierarchy,
          storeLabelTypes,
        });
      })
    )
      .then(setGroupedData)
      .finally(() => setLoading(false));
  }, [companyId, groupBy, data, showInHierarchy]);

  return {
    loading,
    groups: groupedData,
    groupLabels: _.uniqBy(groupedData[0], "groupLabel")
      .map((r) => r.groupLabel)
      .filter(Boolean) as string[],
  };
}

function getPeriod(range: TableDateRangeEnum | undefined): Period | undefined {
  if (!range || range === TableDateRangeEnum.All) return undefined;

  if (range === TableDateRangeEnum.LastMonth) {
    const lastMonth = moment().subtract(1, "month").format("YYYY-MM");
    return {
      type: "monthly",
      range: [lastMonth, lastMonth],
    };
  }
  if (range === TableDateRangeEnum.LastYear) {
    const lastYear = moment().subtract(1, "year").format("YYYY");
    return {
      type: "yearly",
      range: [lastYear, lastYear],
    };
  }
  if (range === TableDateRangeEnum.ThisMonth) {
    const thisMonth = moment().format("YYYY-MM");
    return {
      type: "monthly",
      range: [thisMonth, thisMonth],
    };
  }
  if (range === TableDateRangeEnum.ThisYear) {
    const thisYear = moment().format("YYYY");
    return {
      type: "yearly",
      range: [thisYear, thisYear],
    };
  }
  if (range === TableDateRangeEnum.LastLastMonth) {
    const lastLastMonth = moment().subtract(2, "month").format("YYYY-MM");
    return {
      type: "monthly",
      range: [lastLastMonth, lastLastMonth],
    };
  }
  if (range === TableDateRangeEnum.LastLastYear) {
    const lastLastYear = moment().subtract(2, "year").format("YYYY");
    return {
      type: "yearly",
      range: [lastLastYear, lastLastYear],
    };
  }
  return undefined;
}

export const varsInExpression = /([a-zA-Z]\w*)/g;

export const parseChartExpression = (data: any, expression: string, decimalPlaces = 0) => {
  if (Number.isNaN(decimalPlaces) || typeof decimalPlaces !== 'number') decimalPlaces = 0;
  const safeExpression = expression.replace(varsInExpression, (_, key, offset, fullExpression) => {
    const value = data?.[key] ?? 0;
    if (fullExpression[offset - 2] === "/") {
      return !value ? 1 : value;
    }
    return value as any;
  });
  const result: number = new Function(`return ${safeExpression};`)();
  return Number(result.toFixed(decimalPlaces));
};

export const mergeReportModel = (data: ReportModel[]) => {
  return data.reduce((acc: ReportModel, item) => {
    Object.keys(item).forEach((key) => {
      const k = key as keyof ReportModel;
      if (typeof item[k] === "number") {
        acc[k] = (acc[k] || 0) + (item[k] || 0);
      } else {
        acc[k] = item[k];
      }
    });
    return acc;
  }, {} as ReportModel);
};

export const chartHeight = 300;
